import { useState, type ReactNode } from "react";
import { ServerFirmwarePicker } from "../../components/ServerFirmwarePicker";
import { formatBytes } from "../../components/formatBytes";
import type { AmlogicImageReport } from "../../core/amlogic/AmlogicImageParser";
import type { AmlogicDeviceSession } from "../../core/amlogic/multi/AmlogicDeviceSession";
import { getBatchCounters, isBatchActive, type AmlogicBatchRunSnapshot } from "../../core/amlogic/multi/AmlogicBatchRunStore";
import { AmlogicBatchLogTable } from "./AmlogicBatchLogTable";
import { AmlogicDeviceTable } from "./AmlogicDeviceTable";
import { firmwareSourceSummary, isRemoteFirmwareFile } from "../../core/files/RemoteFirmwareFile";

interface Props {
  generation: "adnl-v3" | "legacy-v2";
  title: string;
  subtitle: string;
  headerActions: ReactNode;
  sessions: readonly AmlogicDeviceSession[];
  selectedIds: ReadonlySet<string>;
  eligibility: ReadonlyMap<string, string | undefined>;
  image?: File;
  report?: AmlogicImageReport;
  sha: string;
  imageBusy: boolean;
  imageMessage: string;
  skipImageCheck: boolean;
  disabledReason?: string;
  batch?: AmlogicBatchRunSnapshot;
  now: number;
  options: ReactNode;
  onSelectFile: (file?: File) => void;
  onToggleSkipImageCheck: (skip: boolean) => void;
  onSelectSession: (id: string, selected: boolean) => void;
  onStart: () => void;
  onStop: () => void;
  onConnectSession: (id: string) => void;
  onDisconnectSession: (id: string) => void;
  onRemoveSession: (id: string) => void;
  onCancelJob: (id: string) => void;
}

export function AmlogicFullPackagePanel(props: Props) {
  const [logFilter, setLogFilter] = useState("all");
  const counters = getBatchCounters(props.batch);
  const active = isBatchActive(props.batch);
  const partitionItems = props.report?.items.filter((item) => item.extension.toUpperCase() === "PARTITION") ?? [];
  const remoteImage = Boolean(props.image && isRemoteFirmwareFile(props.image));

  return (
    <section className={`panel amlogic-full-panel amlogic-full-panel--${props.generation}`} aria-label={props.title}>
      <div className="panel-heading amlogic-full-heading">
        <div><p className="eyebrow">FULL PACKAGE · {props.generation === "adnl-v3" ? "V3" : "V2"}</p><p className="muted small">{props.subtitle}</p></div>
        {props.headerActions}
      </div>

      <div className="amlogic-full-config">
        <div className="amlogic-package-column">
          <ServerFirmwarePicker compact label="Burn package (.img)" accept=".img,application/octet-stream" kind={props.generation === "adnl-v3" ? "v3-package" : "v2-package"} file={props.image} onChange={props.onSelectFile} />
          {props.imageBusy && <p className="muted small">Đang đọc cấu trúc và kiểm tra package…</p>}
          {props.imageMessage && <p className="error-message" role="alert">{props.imageMessage}</p>}
          {props.image && <div className="amlogic-package-meta"><span><b>{props.image.name}</b></span><span>{formatBytes(props.image.size)}</span><span className="amlogic-source-badge">{firmwareSourceSummary(props.image)}</span>{props.report && <span>{props.report.format} · {props.report.recordCount} items</span>}<span>SHA-256: <code>{props.sha ? `${props.sha.slice(0, 16)}…` : props.skipImageCheck ? "bỏ qua" : "—"}</code></span></div>}
          {partitionItems.length > 0 && <details className="amlogic-package-items"><summary>{partitionItems.length} partition trong package</summary><div>{partitionItems.map((item) => <span key={`${item.index}-${item.name}`}><b>{item.name}</b><small>{formatBytes(item.size)}</small></span>)}</div></details>}
        </div>
        <div className="amlogic-options-column">
          <h3>Tùy chọn flash</h3>
          {props.options}
          <label className="amlogic-compact-check" title={remoteImage ? "File server: bỏ qua SHA-256 để không tải toàn bộ firmware về máy." : undefined}><input type="checkbox" checked={remoteImage || props.skipImageCheck} disabled={remoteImage || props.imageBusy} onChange={(event) => props.onToggleSkipImageCheck(event.target.checked)} /> Bỏ Qua CheckImg{remoteImage ? " (server)" : ""}</label>
        </div>
      </div>

      <AmlogicDeviceTable sessions={props.sessions} selectedIds={props.selectedIds} eligibility={props.eligibility} batch={props.batch} now={props.now} onSelect={props.onSelectSession} onConnect={props.onConnectSession} onDisconnect={props.onDisconnectSession} onRemove={props.onRemoveSession} onCancelJob={props.onCancelJob} onSelectLog={setLogFilter} />

      <div className="amlogic-batch-actions">
        <button className="primary" type="button" disabled={Boolean(props.disabledReason) || active} onClick={props.onStart}>Start</button>
        <button className="danger-button" type="button" disabled={!active} onClick={props.onStop}>Stop</button>
        <span className="muted small">{active ? "Đang nhận tự động thiết bị mới cùng thế hệ" : props.disabledReason || (props.selectedIds.size ? `${props.selectedIds.size} thiết bị được chọn` : "Mặc định chưa chọn thiết bị")}</span>
      </div>

      <AmlogicBatchLogTable sessions={props.sessions} batch={props.batch} filter={logFilter} onFilterChange={setLogFilter} />
      <footer className="amlogic-batch-footer" aria-live="polite" aria-atomic="true">
        <span>Tổng <b>{counters.total}</b></span><span>Chờ <b>{counters.queued}</b></span><span>Đang chạy <b>{counters.running}</b></span><span>Thành công <b>{counters.completed}</b></span><span>Lỗi <b>{counters.failed}</b></span><span>Hủy <b>{counters.cancelled}</b></span><span>Bỏ qua <b>{counters.skipped}</b></span>
      </footer>
    </section>
  );
}
